import { useState, useEffect } from 'react';
import { Award, ChevronDown, ChevronUp } from 'lucide-react';
import { supabase } from '../lib/supabase';

// Badge icon mapping
const BADGE_ICONS = {
  'Founding Diver': '🏆',
  'Dive Explorer': '🗺️',
  'Dive Legend': '⭐',
  'Dive Connoisseur': '👑',
  'Friend of Tilted': '🤝',
  'Tilted Ambassador': '📣',
  'Word Spreader': '📢',
  'Buzz Builder': '🐝',
  'Dive Ambassador': '🎖️',
  'Tilted Influencer': '👑',
  'Dive Scout': '🔍',
  'Dive Finder': '🗺️'
};

// Featured badges - the 6 we want to show first (in priority order)
// Founding Diver is always #1
const FEATURED_BADGE_NAMES = [
  'Founding Diver',
  'Friend of Tilted', 
  'Dive Scout',
  'Word Spreader',
  'Dive Explorer',
  'Dive Legend'
];

// Badge tier order - higher requirement = higher tier = should appear first within category
// This ensures Dive Finder (10) appears above Dive Scout (5), etc.
const getBadgeSortPriority = (badge, isEarned) => {
  // Earned badges get much higher priority (lower number = higher priority)
  const earnedBonus = isEarned ? 0 : 1000;
  
  // Founding Diver is always first
  if (badge.name === 'Founding Diver') return earnedBonus + 0;
  
  // Sort by requirement value descending (higher value = more prestigious = higher priority)
  // Invert so higher values get lower priority numbers
  const tierPriority = 500 - (badge.requirement_value || 0);
  
  return earnedBonus + tierPriority;
};

export function BadgeDisplay({ userId, compact = false }) {
  const [badges, setBadges] = useState([]);
  const [allBadges, setAllBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    const fetchBadges = async () => {
      try {
        // Fetch user's earned badges
        const { data: userBadges, error: userBadgesError } = await supabase
          .from('user_badges')
          .select(`
            earned_at,
            badges (
              id,
              name,
              description,
              icon,
              requirement_type,
              requirement_value
            )
          `)
          .eq('user_id', userId);

        if (userBadgesError) {
          console.error('Error fetching user badges:', userBadgesError);
        } else {
          setBadges(userBadges?.map(ub => ({
            ...ub.badges,
            earned_at: ub.earned_at
          })) || []);
        }

        // Fetch all available badges for progress display
        if (!compact) {
          const { data: allBadgesData, error: allBadgesError } = await supabase
            .from('badges')
            .select('*')
            .order('requirement_value', { ascending: false }); // Higher values first

          if (!allBadgesError) {
            setAllBadges(allBadgesData || []);
          }
        }
      } catch (error) {
        console.error('Error fetching badges:', error);
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchBadges();
    } else {
      setLoading(false);
    }
  }, [userId, compact]);

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-6 bg-dive-gray rounded w-24 mb-2" />
        <div className="flex gap-2">
          <div className="w-12 h-12 bg-dive-gray rounded-lg" />
          <div className="w-12 h-12 bg-dive-gray rounded-lg" />
        </div>
      </div>
    );
  }

  // Compact view - just show earned badges in a row
  if (compact) {
    if (badges.length === 0) return null;

    return (
      <div className="flex flex-wrap gap-1">
        {badges.map(badge => (
          <div
            key={badge.id}
            className="text-xl"
            title={`${badge.name}: ${badge.description}`}
          >
            {BADGE_ICONS[badge.name] || badge.icon || '🏅'}
          </div>
        ))}
      </div>
    );
  }

  // Full view with badges and progress
  const earnedBadgeIds = new Set(badges.map(b => b.id));

  // Combine all badges with earned status
  const allBadgesWithStatus = allBadges.map(badge => ({
    ...badge,
    isEarned: earnedBadgeIds.has(badge.id)
  }));

  // Sort all badges: earned first, then by tier (higher requirement = more prestigious)
  const sortedBadges = [...allBadgesWithStatus].sort((a, b) => {
    return getBadgeSortPriority(a, a.isEarned) - getBadgeSortPriority(b, b.isEarned);
  });

  // First 6 badges to show
  const featuredBadges = sortedBadges.slice(0, 6);
  const remainingBadges = sortedBadges.slice(6);
  const hasMoreBadges = remainingBadges.length > 0;

  const renderBadge = (badge) => (
    <div
      key={badge.id}
      className={`bg-dive-darker border rounded-lg p-3 text-center ${
        badge.isEarned 
          ? 'border-dive-amber/50' 
          : 'border-dive-gray opacity-50'
      }`}
    >
      <div className={`text-3xl mb-1 ${!badge.isEarned ? 'grayscale' : ''}`}>
        {BADGE_ICONS[badge.name] || badge.icon || '🏅'}
      </div>
      <p className={`text-sm font-medium truncate ${badge.isEarned ? 'text-white' : 'text-gray-400'}`}>
        {badge.name}
      </p>
      <p className={`text-xs mt-1 line-clamp-2 ${badge.isEarned ? 'text-gray-400' : 'text-gray-500'}`}>
        {badge.description}
      </p>
    </div>
  );

  return (
    <div className="bg-dive-dark border border-dive-gray rounded-xl p-4">
      <h3 className="font-display text-white flex items-center gap-2 mb-4">
        <Award className="w-5 h-5 text-dive-amber" />
        Badges
      </h3>

      {badges.length === 0 && allBadges.length === 0 ? (
        <p className="text-gray-400 text-sm">
          Start rating bars to earn badges!
        </p>
      ) : (
        <>
          {/* Featured 6 badges */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {featuredBadges.map(badge => renderBadge(badge))}
          </div>

          {/* Expanded view - remaining badges */}
          {showAll && remainingBadges.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-3 pt-3 border-t border-dive-gray">
              {remainingBadges.map(badge => renderBadge(badge))}
            </div>
          )}

          {/* See More / See Less button */}
          {hasMoreBadges && (
            <button
              onClick={() => setShowAll(!showAll)}
              className="w-full mt-4 py-2 text-sm text-dive-neon hover:text-white flex items-center justify-center gap-1 transition-colors"
            >
              {showAll ? (
                <>
                  <ChevronUp className="w-4 h-4" />
                  See Less
                </>
              ) : (
                <>
                  <ChevronDown className="w-4 h-4" />
                  See More Badges ({remainingBadges.length})
                </>
              )}
            </button>
          )}
        </>
      )}
    </div>
  );
}

export default BadgeDisplay;
